<template>
  <section class="governance-page" :dir="pageDirection">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>

        <h3>{{ tr("loadingTitle") }}</h3>

        <p>{{ tr("loadingText") }}</p>
      </div>
    </div>

    <div class="page-header">
      <h1>{{ tr("pageTitle") }}</h1>

      <p>{{ tr("pageSubtitle") }}</p>
    </div>

    <div class="top-row">
      <div class="counter">5 {{ tr("indicators") }}</div>

      <select>
        <option>{{ tr("pageTitle") }}</option>
      </select>
    </div>

    <div class="axis-summary">
      <div class="axis-card-header">
        <div>
          <h2>{{ tr("axisTitle") }}</h2>

          <p>{{ tr("axisMeta") }}</p>

          <strong>{{ tr("axisStatement") }}</strong>
        </div>

        <div class="axis-score">
          <span> {{ axisAverage !== null ? `${axisAverage}%` : "-" }}</span>

          <small>
            {{ tr("fisWeight") }}:

            {{ axisWeight !== null ? `${axisWeight}%` : "-" }}</small
          >
        </div>
      </div>

      <div class="axis-stats">
        <div>
          <b>{{ governanceAchievedCount }}</b
          ><span>{{ tr("achieved") }}</span>
        </div>

        <div>
          <b>{{ governanceBelowStandardCount }}</b
          ><span>{{ tr("belowStandard") }}</span>
        </div>

        <div>
          <b>{{ governanceCriticalCount }}</b
          ><span>{{ tr("critical") }}</span>
        </div>
      </div>
    </div>

    <div class="axis-indicator-grid">
      <article
        v-for="kpi in governanceKpis"
        :key="kpi.code"
        class="axis-indicator-card"
        :class="{ flag: kpiStatus(kpi) === 'r' }"
        :data-critical-label="tr('criticalFlag')"
      >
        <div class="axis-indicator-top">
          <span class="code">{{ kpi.code }}</span>

          <span v-if="kpi.composite" class="composite">{{
            tr("composite")
          }}</span>
        </div>

        <h3>{{ kpi.name }}</h3>

        <div class="axis-indicator-value">
          <strong :style="{ color: statusColor(kpi) }">{{ kpi.val }}</strong>

          <span>{{ kpi.unit }}</span>

          <em
            >{{ tr("standard") }} {{ kpi.dir === "lt" ? "≤" : "≥" }}

            {{ kpi.std }}</em
          >
        </div>

        <div class="gauge">
          <div
            :style="{
              width: barWidth(kpi) + '%',

              background: statusColor(kpi),
            }"
          ></div>
        </div>

        <div class="axis-indicator-foot">
          <span>
            {{ tr("achievement") }}:

            <b :style="{ color: statusColor(kpi) }">{{ achieve(kpi) }}%</b>
          </span>

          <span :class="['rag', kpiStatus(kpi)]">
            {{ statusLabel[kpiStatus(kpi)] }}
          </span>
        </div>

        <div v-if="kpi.chart" class="axis-mini-chart">
          <div class="chart-head">
            <span>{{ kpi.chart.label }}</span>

            <span class="chart-badge">{{ chartBadge(kpi) }}</span>
          </div>

          <apexchart
            v-if="chartsReady"
            :type="kpiApex(kpi).type"
            height="260"
            :series="kpiApex(kpi).series"
            :options="kpiApex(kpi).options"
          />
        </div>
      </article>
    </div>

    <div class="axis-card hidden">
      <div class="axis-title">
        <div>
          <span class="dot"></span>

          <strong>{{ tr("dashboardTitle") }}</strong>

          <small>{{ tr("dashboardMeta") }}</small>
        </div>
      </div>

      <p class="desc">{{ tr("axisStatement") }}</p>

      <div class="info-box">
        <p>
          <strong>خطط الطوارئ:</strong>

          تغطية جزئية — التفعيل أثناء الأزمات غير منتظم ·

          <strong>منصات التنسيق:</strong>

          مشاركة فعّالة في الكتلة الصحية واجتماعات الوزارة ·

          <strong>نظام DHIS2:</strong>

          استخدام جزئي — جودة البيانات تحتاج تحسيناً
        </p>
      </div>

      <div class="kpi-grid">
        <div class="kpi-card red">
          <div class="kpi-value">{{ noOfficialManager }}</div>

          <div class="kpi-label">{{ tr("noOfficialManager") }}</div>
        </div>

        <div class="kpi-card orange">
          <div class="kpi-value">{{ noMophProtocols }}</div>

          <div class="kpi-label">{{ tr("noMophProtocols") }}</div>
        </div>

        <div class="kpi-card red">
          <div class="kpi-value">{{ noEmergencyPlan }}</div>

          <div class="kpi-label">{{ tr("noEmergencyPlan") }}</div>
        </div>

        <div class="kpi-card green">
          <div class="kpi-value">{{ formalOrgStructureCount }}</div>

          <div class="kpi-label">{{ tr("formalOrgStructure") }}</div>
        </div>
      </div>

      <div class="charts-grid">
        <div class="chart-card">
          <div class="section-title">{{ tr("orgStructureChart") }}</div>

          <apexchart
            v-if="chartsReady"
            type="donut"
            height="320"
            :series="orgStructureSeries"
            :options="donutOptions(orgStructureLabels)"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">{{ tr("protocolsChart") }}</div>

          <apexchart
            v-if="chartsReady"
            type="bar"
            height="320"
            :series="[{ name: tr('facilityCount'), data: protocolSeries }]"
            :options="barOptions(protocolLabels)"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">{{ tr("accountabilityChart") }}</div>

          <apexchart
            v-if="chartsReady"
            type="donut"
            height="320"
            :series="accountabilitySeries"
            :options="donutOptions(accountabilityLabels)"
          />
        </div>

        <div class="chart-card">
          <div class="section-title">{{ tr("capacityChart") }}</div>

          <apexchart
            v-if="chartsReady"
            type="bar"
            height="320"
            :series="[{ name: tr('occurrenceCount'), data: capacitySeries }]"
            :options="barOptions(capacityLabels)"
          />
        </div>
      </div>

      <div class="table-wrap mb-table">
        <div class="table-title">
          {{ tr("facilityDetails") }}

          <span>{{ governances.length }} {{ tr("facilityUnit") }}</span>
        </div>

        <table class="governance-table">
          <thead>
            <tr>
              <th>{{ tr("facility") }}</th>

              <th>{{ tr("officialManager") }}</th>

              <th>{{ tr("mophProtocols") }}</th>

              <th>{{ tr("emergencyPlan") }}</th>

              <th>{{ tr("orgStructure") }}</th>

              <th>{{ tr("availableProtocols") }}</th>

              <th>{{ tr("accountabilityMechanisms") }}</th>

              <th>{{ tr("capacityBuilding") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in governances" :key="item.id">
              <td class="name">{{ item.facility_name }}</td>

              <td>
                <span
                  :class="[
                    'status-badge',

                    yesNoClass(item.has_official_manager),
                  ]"
                >
                  {{ displayYesNo(item.has_official_manager) }}
                </span>
              </td>

              <td>
                <span
                  :class="['status-badge', yesNoClass(item.has_moph_protocols)]"
                >
                  {{ displayYesNo(item.has_moph_protocols) }}
                </span>
              </td>

              <td>
                <span
                  :class="[
                    'status-badge',

                    yesNoClass(item.has_emergency_response_plan),
                  ]"
                >
                  {{ displayYesNo(item.has_emergency_response_plan) }}
                </span>
              </td>

              <td>
                <span
                  :class="[
                    'status-badge',

                    yesNoClass(item.has_formal_org_structure),
                  ]"
                >
                  {{ displayYesNo(item.has_formal_org_structure) }}
                </span>
              </td>

              <td>{{ item.available_protocols_label }}</td>

              <td>{{ item.accountability_mechanisms_label }}</td>

              <td>{{ item.capacity_building_needs_label }}</td>
            </tr>

            <tr v-if="!loading && !governances.length">
              <td colspan="8" class="empty-cell">
                {{ tr("noGovernanceData") }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="mobile-governance-cards">
          <article
            v-for="item in governances"
            :key="`mobile-${item.id}`"
            class="mobile-governance-card"
          >
            <div class="mobile-card-head">
              <h3>{{ item.facility_name }}</h3>
            </div>

            <div class="mobile-card-body">
              <div class="mobile-data-row">
                <span>{{ tr("officialManager") }}</span
                ><span
                  :class="[
                    'status-badge',

                    yesNoClass(item.has_official_manager),
                  ]"
                  >{{ displayYesNo(item.has_official_manager) }}</span
                >
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("mophProtocols") }}</span
                ><span
                  :class="['status-badge', yesNoClass(item.has_moph_protocols)]"
                  >{{ displayYesNo(item.has_moph_protocols) }}</span
                >
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("emergencyPlan") }}</span
                ><span
                  :class="[
                    'status-badge',

                    yesNoClass(item.has_emergency_response_plan),
                  ]"
                  >{{ displayYesNo(item.has_emergency_response_plan) }}</span
                >
              </div>

              <div class="mobile-data-row">
                <span>{{ tr("orgStructure") }}</span
                ><span
                  :class="[
                    'status-badge',

                    yesNoClass(item.has_formal_org_structure),
                  ]"
                  >{{ displayYesNo(item.has_formal_org_structure) }}</span
                >
              </div>

              <div class="mobile-data-row stacked">
                <span>{{ tr("availableProtocols") }}</span
                ><strong>{{ item.available_protocols_label }}</strong>
              </div>

              <div class="mobile-data-row stacked">
                <span>{{ tr("accountabilityMechanisms") }}</span
                ><strong>{{ item.accountability_mechanisms_label }}</strong>
              </div>

              <div class="mobile-data-row stacked">
                <span>{{ tr("capacityBuilding") }}</span
                ><strong>{{ item.capacity_building_needs_label }}</strong>
              </div>
            </div>
          </article>

          <div v-if="!loading && !governances.length" class="mobile-empty">
            {{ tr("noGovernanceData") }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const isArabic = computed(() => locale.value === "ar");

const tr = (key, params = {}) => t(`governancePage.${key}`, params);

const chartFont = computed(() => (isArabic.value ? "Cairo" : "Inter"));

const loading = ref(false);

const chartsReady = ref(false);

const governances = ref([]);

const gov01Components = ref([]);

const gov02Components = ref([]);

const gov03Components = ref([]);

const gov04Components = ref([]);

const gov05Components = ref([]);

const statusLabel = computed(() => ({
  g: tr("compliant"),

  a: tr("near"),

  r: tr("gap"),
}));

const colors = { g: "#10b981", a: "#f59e0b", r: "#ef4444" };

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);

  return Number.isFinite(n) ? n : fallback;
};

const round1 = (v) => Number(safeNumber(v).toFixed(1));

const componentName = (x) =>
  isArabic.value
    ? x.criterion_ar ||
      x.component_ar ||
      x.label_ar ||
      x.label ||
      x.name ||
      tr("unspecified")
    : x.criterion_en ||
      x.component_en ||
      x.label_en ||
      x.label ||
      x.name ||
      tr("unspecified");

const componentPercent = (x) => {
  const avg = safeNumber(x.avg_score);

  const max = safeNumber(x.max_score, 100);

  return max > 0 ? round1((avg / max) * 100) : 0;
};

const gov01Meta = ref(null);

const gov01Facilities = ref([]);

const gov01core = ref(0);

const gov02core = ref(0);

const gov03core = ref(0);

const gov04core = ref(0);

const gov05Score = ref(0);

// const gov01Value = computed(() => avgComponents(gov01Components.value));

// const gov02Value = computed(() => avgComponents(gov02Components.value));

// const gov03Value = computed(() => avgComponents(gov03Components.value));

const gov01Value = computed(() => gov01core.value);

const gov02Value = computed(() => gov02core.value);

const gov03Value = computed(() => gov03core.value);

const gov04Value = computed(() => gov04core.value);

const gov05Value = computed(() => gov05Score.value);

const governanceCharts = computed(() => ({
  "GOV-01": {
    type: "radar",

    label: tr("charts.governanceDimensions"),

    data: gov01Components.value.map(componentPercent),

    labels: gov01Components.value.map(componentName),
  },

  "GOV-02": {
    type: "bar",

    label: tr("charts.hmisReadiness"),

    data: gov02Components.value.map(componentPercent),

    labels: gov02Components.value.map(componentName),
  },

  "GOV-03": {
    type: "radialBar",

    label: tr("charts.reportCompletion"),

    data: gov03Components.value.map(componentPercent),

    labels: gov03Components.value.map(componentName),
  },

  "GOV-04": {
    type: "radialBar",

    label: tr("charts.communityAccountability"),

    data: gov04Components.value.map(componentPercent),

    labels: gov04Components.value.map(componentName),
  },

  "GOV-05": {
    type: "bar",

    label: tr("charts.emergencyReadiness"),

    data: gov05Components.value.map(componentPercent),

    labels: gov05Components.value.map(componentName),
  },
}));

const governanceKpis = computed(() => [
  {
    code: "GOV-01",

    name: tr("kpis.institutionalGovernance"),

    val: gov01Value.value,

    unit: "/100",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: false,

    chart: governanceCharts.value["GOV-01"],
  },

  {
    code: "GOV-02",

    name: tr("kpis.hmis"),

    val: gov02Value.value,

    unit: "/100",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: true,

    chart: governanceCharts.value["GOV-02"],
  },

  {
    code: "GOV-03",

    name: tr("kpis.reportCompletion"),

    val: gov03Value.value,

    unit: "%",

    std: 90,

    dir: "gt",

    max: 100,

    composite: true,

    flag: true,

    chart: governanceCharts.value["GOV-03"],
  },

  {
    code: "GOV-04",

    name: tr("kpis.communityAccountability"),

    val: gov04Value.value,

    unit: "/100",

    std: 70,

    dir: "gt",

    max: 100,

    composite: true,

    flag: false,

    chart: governanceCharts.value["GOV-04"],
  },

  {
    code: "GOV-05",

    name: tr("kpis.emergencyPreparedness"),

    val: gov05Value.value,

    unit: "/100",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: true,

    chart: governanceCharts.value["GOV-05"],
  },
]);

const governanceAchievedCount = computed(
  () => governanceKpis.value.filter((k) => kpiStatus(k) === "g").length
);

const governanceBelowStandardCount = computed(
  () => governanceKpis.value.filter((k) => kpiStatus(k) === "a").length
);

const governanceCriticalCount = computed(
  () => governanceKpis.value.filter((k) => kpiStatus(k) === "r").length
);

const kpiStatus = (kpi) => {
  const v = safeNumber(kpi.val);

  const std = safeNumber(kpi.std);

  if (kpi.dir === "lt") {
    if (v <= std) return "g";

    // حرج عندما تتجاوز القيمة المعيار بنسبة 30% أو أكثر.
    if (v < std * 1.3) return "a";

    return "r";
  }

  if (v >= std) return "g";

  // حرج عندما تقل القيمة عن المعيار بنسبة 30% أو أكثر.
  if (v > std * 0.7) return "a";

  return "r";
};

const statusColor = (kpi) => colors[kpiStatus(kpi)];

const achieve = (kpi) => {
  const v = safeNumber(kpi.val);

  const std = safeNumber(kpi.std, 1);

  const max = safeNumber(kpi.max, 100);

  if (kpi.dir === "lt") {
    return v <= std
      ? 100
      : Math.max(
          0,

          Math.round(100 - ((v - std) / Math.max(1, max - std)) * 100)
        );
  }

  return Math.min(100, Math.round((v / std) * 100));
};

const barWidth = (kpi) =>
  Math.min(
    100,

    Math.max(3, (safeNumber(kpi.val) / safeNumber(kpi.max, 100)) * 100)
  );

const chartBadge = (kpi) => {
  if (kpi.code === "GOV-01") return tr("chartTypes.radar");

  if (["GOV-03", "GOV-04"].includes(kpi.code)) return tr("chartTypes.radial");

  return tr("chartTypes.columns", { count: kpi.chart?.labels?.length || 0 });
};

const darkChartBase = {
  chart: {
    toolbar: { show: false },

    fontFamily: chartFont.value,

    foreColor: "#7f9abb",
  },

  grid: { borderColor: "#243855" },

  dataLabels: { enabled: false },

  legend: { show: false },

  noData: { text: tr("noData") },
};

const getKpiColor = (value, std) => {
  const p = (safeNumber(value) / safeNumber(std, 1)) * 100;

  if (p >= 100) return "#10b981";

  if (p >= 75) return "#f59e0b";

  return "#ef4444";
};

const gaugeOptions = (value, std) => ({
  ...darkChartBase,

  chart: {
    ...darkChartBase.chart,

    type: "radialBar",

    sparkline: { enabled: true },
  },

  colors: [getKpiColor(value, std)],

  plotOptions: {
    radialBar: {
      startAngle: -90,

      endAngle: 90,

      hollow: { size: "60%" },

      track: {
        background: "#19304f",

        strokeWidth: "100%",
      },

      dataLabels: {
        name: {
          show: true,

          offsetY: 34,

          color: "#6b89aa",

          fontSize: "12px",

          formatter: () => `${tr("standard")} ≥ ${std}%`,
        },

        value: {
          offsetY: -5,

          fontSize: "30px",

          fontWeight: 900,

          color: getKpiColor(value, std),

          formatter: () => `${safeNumber(value).toFixed(1)}%`,
        },
      },
    },
  },

  stroke: { lineCap: "butt" },

  tooltip: { enabled: false },
});

const barDarkOptions = (labels = [], values = [], std = 80) => ({
  ...darkChartBase,

  chart: {
    ...darkChartBase.chart,

    type: "bar",
  },

  plotOptions: {
    bar: {
      distributed: true,

      borderRadius: 5,

      columnWidth: "52%",
    },
  },

  colors: values.map((v) => getKpiColor(v, std)),

  xaxis: {
    categories: labels.length ? labels : [tr("noData")],

    labels: {
      rotate: -60,

      trim: true,

      style: {
        colors: "#8fa8ca",

        fontSize: "10px",
      },
    },

    axisBorder: { color: "#243855" },

    axisTicks: { color: "#243855" },
  },

  yaxis: {
    min: 0,

    max: 100,

    tickAmount: 5,

    labels: {
      style: {
        colors: "#8fa8ca",

        fontSize: "11px",
      },
    },
  },

  tooltip: {
    theme: "dark",

    y: {
      formatter: (val) => `${safeNumber(val).toFixed(1)}%`,
    },
  },
});

const radarOptions = (labels = []) => ({
  ...darkChartBase,

  chart: {
    ...darkChartBase.chart,

    type: "radar",
  },

  colors: ["#f59e0b"],

  stroke: {
    width: 2,

    colors: ["#f59e0b"],
  },

  fill: {
    opacity: 0.22,

    colors: ["#f59e0b"],
  },

  markers: {
    size: 4,

    colors: ["#f59e0b"],

    strokeColors: "#f59e0b",
  },

  xaxis: {
    categories: labels.length ? labels : [tr("noData")],

    labels: {
      style: {
        colors: "#9fb6d6",

        fontSize: "11px",
      },
    },
  },

  yaxis: {
    show: false,

    min: 0,

    max: 100,
  },

  plotOptions: {
    radar: {
      polygons: {
        strokeColors: "#243855",

        connectorColors: "#243855",

        fill: {
          colors: ["#162238", "#162238"],
        },
      },
    },
  },

  tooltip: { theme: "dark" },
});

const kpiApex = (kpi) => {
  const ch = kpi.chart;

  if (kpi.code === "GOV-01") {
    return {
      type: "radar",

      series: [
        {
          name: kpi.name,

          data: ch?.data?.length ? ch.data : [0],
        },
      ],

      options: radarOptions(ch?.labels || []),
    };
  }

  if (["GOV-03", "GOV-04"].includes(kpi.code)) {
    return {
      type: "radialBar",

      series: [safeNumber(kpi.val)],

      options: gaugeOptions(safeNumber(kpi.val), safeNumber(kpi.std, 80)),
    };
  }

  return {
    type: "bar",

    series: [
      {
        name: kpi.code,

        data: ch?.data?.length ? ch.data : [0],
      },
    ],

    options: barDarkOptions(ch?.labels || [], ch?.data || [], kpi.std),
  };
};

const fetchGov01Components = async () => {
  const res = await api.get("/health-facilities/gov01Components");

  const data = res.data || {};

  gov01Value.value = safeNumber(data.value);

  gov01Components.value = Array.isArray(data.components) ? data.components : [];

  gov01Facilities.value = Array.isArray(data.by_facility)
    ? data.by_facility
    : [];

  gov01Meta.value = {
    success: data.success,

    indicator: data.indicator,

    title: data.title,

    standard: data.standard,

    unit: data.unit,

    facilities: data.facilities,

    classification: data.classification || {},
  };

  gov01core.value = safeNumber(res.data?.Gvg, 0);
};

const fetchGov02Components = async () => {
  const res = await api.get("/health-facilities/gov02Components");

  gov02Components.value = res.data?.data || [];

  gov02core.value = safeNumber(res.data?.Gvg, 0);
};

const fetchGov03Components = async () => {
  const res = await api.get("/health-facilities/gov03Components");

  gov03Components.value = res.data?.data || [];

  gov03core.value = safeNumber(res.data?.Gvg, 0);
};

const fetchGov04Components = async () => {
  const res = await api.get("/health-facilities/gov04Components");

  gov04Components.value = res.data?.data || [];

  gov04core.value = safeNumber(res.data?.Gvg, 0);
};

const fetchGov05Components = async () => {
  const res = await api.get("/health-facilities/gov05Components");

  gov05Components.value = res.data?.data || [];

  gov05Score.value = safeNumber(res.data?.Gvg, 0);
};

const cleanValue = (value) => {
  if (value === null || value === undefined || value === "") return "غير محدد";

  return String(value);
};

const yesNoMap = {
  yes: "نعم",

  no: "لا",

  partial: "جزئي",

  true: "نعم",

  false: "لا",

  1: "نعم",

  0: "لا",

  نعم: "نعم",

  لا: "لا",

  جزئي: "جزئي",

  "غير محدد": "غير محدد",
};

const displayYesNo = (value) => {
  const normalized = String(value ?? "")
    .trim()

    .toLowerCase();

  if (["نعم", "yes", "true", "1"].includes(normalized)) return tr("yes");

  if (["جزئي", "partial"].includes(normalized)) return tr("partial");

  if (["لا", "no", "false", "0"].includes(normalized)) return tr("no");

  return tr("unspecified");
};

const protocolMap = {
  moph: "بروتوكولات وزارة الصحة",

  emergency: "بروتوكولات الطوارئ",

  infection_control: "مكافحة العدوى",

  referral: "الإحالة",

  pharmacy: "الصيدلة",

  maternal_child: "الأمومة والطفولة",

  nutrition: "التغذية",

  vaccination: "التحصين",

  none: "لا يوجد",

  "غير محدد": "غير محدد",
};

const accountabilityMap = {
  complaints_box: "صندوق شكاوى",

  community_committee: "لجنة مجتمعية",

  hotline: "خط ساخن",

  patient_feedback: "ملاحظات المرضى",

  regular_meetings: "اجتماعات دورية",

  none: "لا يوجد",

  "غير محدد": "غير محدد",
};

const capacityMap = {
  management: "الإدارة",

  hmis: "نظام المعلومات الصحية",

  reporting: "التقارير",

  emergency_preparedness: "التأهب للطوارئ",

  financial_management: "الإدارة المالية",

  quality_improvement: "تحسين الجودة",

  infection_control: "مكافحة العدوى",

  none: "لا يوجد",

  "غير محدد": "غير محدد",
};

const translateValue = (value, map) => {
  const key = cleanValue(value);

  return map[key] || key;
};

const translateMulti = (value, map) => {
  const key = cleanValue(value);

  if (key === "غير محدد") return key;

  return key

    .split(",")

    .map((item) => {
      const cleanItem = item.trim();

      return map[cleanItem] || cleanItem;
    })

    .join("، ");
};

const fetchGovernances = async () => {
  try {
    const [governanceRes, facilitiesRes] = await Promise.all([
      api.get("/facility-governances"),

      api.get("/health-facilities"),
    ]);

    const governanceList = Array.isArray(governanceRes.data)
      ? governanceRes.data
      : governanceRes.data?.data || [];

    const facilitiesList = Array.isArray(facilitiesRes.data)
      ? facilitiesRes.data
      : facilitiesRes.data?.data || [];

    const facilityMap = {};

    facilitiesList.forEach((facility, index) => {
      const surveyId = String(facility.survey_id || facility.id || index + 1);

      facilityMap[surveyId] = isArabic.value
        ? facility.facility_name_ar ||
          facility.facility_name ||
          facility.name ||
          `${tr("facilityUnit")} #${surveyId}`
        : facility.facility_name_en ||
          facility.facility_name_ar ||
          facility.facility_name ||
          facility.name ||
          `${tr("facilityUnit")} #${surveyId}`;
    });

    governances.value = governanceList.map((item) => {
      const surveyId = String(item.survey_id);

      return {
        id: item.id,

        survey_id: item.survey_id,

        facility_name:
          facilityMap[surveyId] ||
          item.facility_name ||
          `${tr("survey")} #${item.survey_id}`,

        has_official_manager: translateValue(
          item.has_official_manager ||
            item.official_manager ||
            item.has_manager,

          yesNoMap
        ),

        has_moph_protocols: translateValue(
          item.has_moph_protocols || item.moph_protocols || item.has_protocols,

          yesNoMap
        ),

        has_emergency_response_plan: translateValue(
          item.has_emergency_response_plan ||
            item.emergency_response_plan ||
            item.has_emergency_plan,

          yesNoMap
        ),

        has_formal_org_structure: translateValue(
          item.has_formal_org_structure ||
            item.formal_org_structure ||
            item.has_org_structure,

          yesNoMap
        ),

        available_protocols_label: translateMulti(
          item.available_protocols,

          protocolMap
        ),

        accountability_mechanisms_label: translateMulti(
          item.accountability_mechanisms,

          accountabilityMap
        ),

        capacity_building_needs_label: translateMulti(
          item.capacity_building_needs,

          capacityMap
        ),
      };
    });
  } catch (error) {
    console.error("Facility Governance API Error:", error);

    governances.value = [];
  }
};

const axisWeight = ref(null);

const axisAverage = ref(null);

const loadAxisWeight = async () => {
  try {
    const response = await api.get("/health-facilities/axis-averages");

    const payload = response.data?.data;

    axisWeight.value = payload?.governance?.weight ?? null;

    axisAverage.value = payload?.governance?.average ?? null;
  } catch (error) {
    console.error("خطأ في تحميل بيانات المحاور:", error);

    axisWeight.value = null;

    axisAverage.value = null;
  }
};

const loadPage = async () => {
  try {
    loading.value = true;

    chartsReady.value = false;

    await Promise.all([
      fetchGovernances(),

      fetchGov01Components(),

      fetchGov02Components(),

      fetchGov03Components(),

      fetchGov04Components(),

      fetchGov05Components(),

      loadAxisWeight(),
    ]);
  } catch (error) {
    console.error("Governance Page Error:", error);
  } finally {
    chartsReady.value = true;

    loading.value = false;
  }
};

onMounted(loadPage);

const noOfficialManager = computed(
  () => governances.value.filter((i) => i.has_official_manager === "لا").length
);

const noMophProtocols = computed(
  () => governances.value.filter((i) => i.has_moph_protocols === "لا").length
);

const noEmergencyPlan = computed(
  () =>
    governances.value.filter((i) => i.has_emergency_response_plan === "لا")
      .length
);

const formalOrgStructureCount = computed(
  () =>
    governances.value.filter((i) => i.has_formal_org_structure === "نعم").length
);

const countBy = (field) => {
  const result = {};

  governances.value.forEach((item) => {
    const value = cleanValue(item[field]);

    result[value] = (result[value] || 0) + 1;
  });

  return result;
};

const countMultiLabel = (field) => {
  const result = {};

  governances.value.forEach((item) => {
    cleanValue(item[field])
      .split("،")

      .forEach((value) => {
        const key = value.trim() || "غير محدد";

        result[key] = (result[key] || 0) + 1;
      });
  });

  return result;
};

const orgStructureData = computed(() => countBy("has_formal_org_structure"));

const protocolData = computed(() =>
  countMultiLabel("available_protocols_label")
);

const accountabilityData = computed(() =>
  countMultiLabel("accountability_mechanisms_label")
);

const capacityData = computed(() =>
  countMultiLabel("capacity_building_needs_label")
);

const orgStructureLabels = computed(() => Object.keys(orgStructureData.value));

const orgStructureSeries = computed(() =>
  Object.values(orgStructureData.value)
);

const protocolLabels = computed(() => Object.keys(protocolData.value));

const protocolSeries = computed(() => Object.values(protocolData.value));

const accountabilityLabels = computed(() =>
  Object.keys(accountabilityData.value)
);

const accountabilitySeries = computed(() =>
  Object.values(accountabilityData.value)
);

const capacityLabels = computed(() => Object.keys(capacityData.value));

const capacitySeries = computed(() => Object.values(capacityData.value));

const donutOptions = (labels = []) => ({
  chart: {
    type: "donut",

    toolbar: { show: false },

    fontFamily: chartFont.value,
  },

  labels: labels.length ? labels : [tr("unspecified")],

  legend: { position: "bottom" },

  dataLabels: { enabled: true },

  noData: { text: tr("noData") },
});

const shortLabel = (text, max = 14) => {
  if (!text) return "غير محدد";

  return String(text).length > max
    ? `${String(text).substring(0, max)}...`
    : text;
};

const barOptions = (labels = []) => ({
  chart: {
    type: "bar",

    toolbar: { show: false },

    fontFamily: chartFont.value,
  },

  xaxis: {
    categories: labels.length ? labels : [tr("unspecified")],

    labels: {
      rotate: -35,

      trim: true,

      formatter: (value) => shortLabel(value, 14),

      style: { fontSize: "11px" },
    },
  },

  tooltip: {
    x: {
      formatter: (value, opts) => labels[opts.dataPointIndex] || value,
    },
  },

  plotOptions: {
    bar: {
      borderRadius: 6,

      columnWidth: "45%",
    },
  },

  dataLabels: { enabled: false },

  noData: { text: tr("noData") },
});

const yesNoClass = (value) => {
  if (value === "نعم") return "status-green";

  if (value === "جزئي") return "status-orange";

  if (value === "غير محدد") return "status-gray";

  return "status-red";
};
</script>

<style scoped>
.governance-page {
  min-height: 100vh;

  background: #f4f6f9;

  padding: 24px;

  color: #071126;

  font-family: "Inter", "Cairo", sans-serif;
}

.page-loading {
  position: fixed;

  inset: 0;

  background: rgba(244, 246, 249, 0.92);

  backdrop-filter: blur(6px);

  z-index: 9999;

  display: flex;

  align-items: center;

  justify-content: center;
}

.loader-card {
  width: 320px;

  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 22px;

  padding: 34px 24px;

  text-align: center;

  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;

  height: 64px;

  border: 6px solid #e2e8f0;

  border-top-color: #be185d;

  border-radius: 50%;

  margin: 0 auto 18px;

  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  font-size: 18px;

  font-weight: 900;

  color: #be185d;

  margin-bottom: 8px;
}

.loader-card p {
  color: #64748b;

  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  text-align: right;

  margin-bottom: 26px;
}

.page-header h1 {
  font-size: 28px;

  font-weight: 900;

  margin-bottom: 8px;

  color: #061226;
}

.page-header p {
  color: #52657d;

  font-size: 14px;
}

.top-row {
  display: flex;

  align-items: center;

  justify-content: space-between;

  margin-bottom: 18px;
}

.counter {
  color: #64748b;

  font-size: 14px;
}

.top-row select {
  width: 225px;

  height: 50px;

  background: #fff;

  border: 1px solid #cbd5e1;

  border-radius: 8px;

  padding: 0 14px;

  font-family: inherit;
}

.axis-summary,
.measured-box {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 18px;

  padding: 18px;

  margin-bottom: 18px;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.axis-card-header {
  display: flex;

  justify-content: space-between;

  gap: 20px;

  align-items: center;
}

.axis-card-header h2 {
  font-size: 22px;

  font-weight: 900;

  color: #be185d;

  margin-bottom: 6px;
}

.axis-card-header p {
  color: #64748b;

  font-size: 13px;

  margin-bottom: 8px;
}

.axis-card-header strong {
  color: #071126;

  font-size: 14px;
}

.axis-score {
  min-width: 130px;

  text-align: center;

  background: #fce7f3;

  color: #be185d;

  border-radius: 18px;

  padding: 18px;
}

.axis-score span {
  display: block;

  font-size: 34px;

  font-weight: 900;
}

.axis-score small {
  font-weight: 800;
}

.axis-stats {
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 12px;

  margin-top: 16px;
}

.axis-stats div {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 14px;

  text-align: center;
}

.axis-stats b {
  display: block;

  font-size: 26px;

  color: #be185d;
}

.axis-stats span {
  font-size: 13px;

  font-weight: 800;

  color: #64748b;
}

.measured-grid {
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 14px;
}

.measure-card {
  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 14px;

  padding: 15px;
}

.measure-card span {
  display: block;

  min-height: 42px;

  font-size: 13px;

  font-weight: 800;

  color: #334155;
}

.measure-card b {
  display: block;

  font-size: 30px;

  font-weight: 900;

  color: #be185d;

  margin: 8px 0;
}

.measure-card small,
.section-sub {
  color: #64748b;

  font-weight: 700;
}

.section-sub {
  font-size: 13px;

  margin-bottom: 14px;
}

.axis-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 22px;

  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.axis-title {
  border-bottom: 1px solid #dbe3ee;

  padding-bottom: 14px;

  margin-bottom: 16px;
}

.axis-title > div {
  display: flex;

  align-items: center;

  gap: 8px;

  flex-wrap: wrap;
}

.dot {
  width: 12px;

  height: 12px;

  background: #be185d;

  border-radius: 4px;
}

.axis-title strong {
  font-size: 18px;

  font-weight: 900;
}

.axis-title small {
  font-size: 12px;

  color: #64748b;
}

.desc {
  color: #64748b;

  font-size: 14px;

  margin-bottom: 14px;
}

.info-box {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 16px 18px;

  margin-bottom: 12px;

  line-height: 2;

  font-size: 13px;

  color: #334155;
}

.info-box strong {
  color: #be185d;
}

.kpi-grid {
  display: grid;

  grid-template-columns: repeat(4, 1fr);

  gap: 14px;

  margin: 18px 0;
}

.kpi-card {
  border-radius: 16px;

  padding: 20px;

  text-align: center;

  border: 1px solid #dbe3ee;
}

.kpi-value {
  font-size: 28px;

  font-weight: 900;
}

.kpi-label {
  margin-top: 8px;

  font-size: 13px;

  font-weight: 800;
}

.kpi-card.red {
  background: #fdecea;

  color: #c62828;
}

.kpi-card.orange {
  background: #fff4e5;

  color: #d4821a;
}

.kpi-card.green {
  background: #e8f5e9;

  color: #1b5e20;
}

.charts-grid {
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 14px;

  margin: 18px 0;
}

.chart-card {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 12px;

  padding: 16px;

  min-height: 370px;
}

.section-title {
  font-size: 16px;

  font-weight: 900;

  color: #be185d;

  margin-bottom: 12px;
}

.table-wrap {
  overflow-x: auto;

  border: 1px solid #dbe3ee;

  border-radius: 10px;

  background: #fff;
}

.mb-table {
  margin-bottom: 18px;
}

.table-title {
  background: #be185d;

  color: #fff;

  padding: 14px 18px;

  font-weight: 800;

  display: flex;

  justify-content: space-between;

  align-items: center;
}

.table-title span {
  background: rgba(255, 255, 255, 0.15);

  padding: 4px 10px;

  border-radius: 999px;

  font-size: 12px;
}

table {
  width: 100%;

  min-width: 1100px;

  border-collapse: collapse;

  background: #fff;
}

.governance-table {
  min-width: 1200px;
}

th {
  background: #f8fafc;

  color: #be185d;

  font-size: 13px;

  font-weight: 900;

  padding: 15px 14px;

  text-align: right;

  white-space: nowrap;

  border-bottom: 1px solid #e2e8f0;
}

td {
  padding: 14px;

  border-bottom: 1px solid #dbe3ee;

  font-size: 13px;

  line-height: 1.8;

  vertical-align: top;
}

tbody tr:hover {
  background: #f8fafc;
}

.code {
  font-weight: 900;

  direction: ltr;

  text-align: center;

  white-space: nowrap;

  color: #be185d;
}

.name {
  font-weight: 900;

  min-width: 220px;
}

.status-badge {
  display: inline-flex;

  align-items: center;

  gap: 7px;

  border-radius: 999px;

  padding: 7px 13px;

  font-size: 12px;

  font-weight: 700;
}

.status-green {
  background: #dcfce7;

  color: #166534;
}

.status-orange {
  background: #ffedd5;

  color: #9a3412;
}

.status-red {
  background: #fee2e2;

  color: #991b1b;
}

.status-gray {
  background: #f1f5f9;

  color: #475569;
}

.empty-cell {
  text-align: center;

  color: #64748b;

  padding: 20px !important;
}

.axis-indicator-grid {
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

  gap: 14px;

  margin-bottom: 18px;
}

.axis-indicator-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 16px;

  position: relative;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.axis-indicator-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.axis-indicator-card.flag::after {
  content: attr(data-critical-label);

  position: absolute;

  top: 13px;

  left: 14px;

  font-size: 10px;

  color: #ef4444;

  font-weight: 900;
}

.axis-indicator-top {
  display: flex;

  gap: 8px;

  margin-bottom: 10px;
}

.axis-indicator-card h3 {
  font-size: 15px;

  font-weight: 900;

  color: #071126;

  margin: 0 0 12px;

  min-height: 42px;
}

.axis-indicator-value {
  display: flex;

  align-items: baseline;

  gap: 7px;

  margin-bottom: 10px;
}

.axis-indicator-value strong {
  font-size: 32px;

  font-weight: 900;

  direction: ltr;
}

.axis-indicator-value span {
  color: #64748b;
}

.axis-indicator-value em {
  margin-right: auto;

  font-style: normal;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  padding: 5px 9px;

  color: #475569;

  font-size: 11px;

  font-weight: 800;
}

.axis-indicator-foot {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin: 8px 0 13px;

  font-size: 12px;

  font-weight: 700;

  color: #64748b;
}

.composite {
  background: #f5f3ff;

  border: 1px solid #ddd6fe;

  color: #7c3aed;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 10px;

  font-weight: 900;
}

.gauge {
  height: 9px;

  background: #e2e8f0;

  border-radius: 999px;

  overflow: hidden;
}

.gauge div {
  height: 100%;

  border-radius: 999px;
}

.rag {
  border-radius: 7px;

  padding: 4px 10px;

  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);

  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);

  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);

  color: #ef4444;
}

@media (max-width: 1000px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .kpi-grid,
  .measured-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .governance-page {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .top-row {
    flex-direction: column-reverse;

    align-items: stretch;

    gap: 12px;
  }

  .top-row select {
    width: 100%;
  }

  .axis-card {
    padding: 14px;
  }

  .axis-stats {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .kpi-grid,
  .measured-grid {
    grid-template-columns: 1fr;
  }
}

.axis-mini-chart {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 14px;

  height: 300px;

  margin-top: 14px;

  overflow: hidden;
}

.chart-head {
  display: flex;

  justify-content: space-between;

  align-items: center;

  gap: 10px;

  color: #334155;

  font-size: 12px;

  font-weight: 800;

  margin-bottom: 8px;
}

.chart-head span:first-child {
  line-height: 1.7;

  color: #334155;
}

.chart-badge {
  background: #fdf2f8;

  border: 1px solid #fbcfe8;

  color: #be185d;

  border-radius: 7px;

  padding: 4px 9px;

  font-size: 10px;

  white-space: nowrap;

  font-weight: 900;
}

/* ===========================

   ApexCharts - Light Theme

\=========================== */

.axis-mini-chart {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 14px;

  height: 280px;

  margin-top: 16px;

  overflow: hidden;
}

.chart-head {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin-bottom: 12px;

  color: #334155;

  font-size: 13px;

  font-weight: 800;
}

.chart-badge {
  background: #f1f5f9;

  border: 1px solid #dbe3ee;

  color: #475569;

  border-radius: 999px;

  padding: 5px 10px;

  font-size: 11px;

  font-weight: 700;
}

/* خطوط الرسم */

.axis-mini-chart :deep(.apexcharts-gridline),
.axis-mini-chart :deep(.apexcharts-xaxis line),
.axis-mini-chart :deep(.apexcharts-yaxis line),
.axis-mini-chart :deep(.apexcharts-xaxis-tick),
.axis-mini-chart :deep(.apexcharts-yaxis-tick) {
  stroke: #e2e8f0 !important;
}

/* النصوص */

.axis-mini-chart :deep(.apexcharts-text),
.axis-mini-chart :deep(.apexcharts-xaxis-label),
.axis-mini-chart :deep(.apexcharts-yaxis-label),
.axis-mini-chart :deep(.apexcharts-legend-text) {
  fill: #64748b !important;

  color: #64748b !important;

  font-family: "IBM Plex Sans Arabic", sans-serif !important;

  font-size: 11px !important;
}

/* Radar */

.axis-mini-chart :deep(.apexcharts-radar-series polygon),
.axis-mini-chart :deep(.apexcharts-polygon) {
  stroke: #dbe3ee !important;

  fill: transparent !important;
}

/* Gauge Track */

.axis-mini-chart :deep(.apexcharts-radialbar-track path) {
  stroke: #e2e8f0 !important;
}

/* Tooltip */

.axis-mini-chart :deep(.apexcharts-tooltip) {
  background: #ffffff !important;

  border: 1px solid #dbe3ee !important;

  border-radius: 12px !important;

  color: #0f172a !important;

  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12) !important;
}

.axis-mini-chart :deep(.apexcharts-tooltip-title) {
  background: #f8fafc !important;

  color: #0f172a !important;

  border-bottom: 1px solid #e2e8f0 !important;
}

/* Data Labels */

.axis-mini-chart :deep(.apexcharts-datalabel),
.axis-mini-chart :deep(.apexcharts-data-labels text) {
  fill: #0f172a !important;

  font-weight: 700 !important;
}

/* Hover */

.axis-mini-chart:hover {
  border-color: #cbd5e1;

  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);

  transition: all 0.25s;
}

/* ================= RTL / LTR + Mobile Cards ================= */

.governance-page[dir="rtl"] {
  direction: rtl;

  font-family: "Cairo", sans-serif;
}

.governance-page[dir="ltr"] {
  direction: ltr;

  font-family: "Inter", "Cairo", sans-serif;
}

.governance-page[dir="rtl"] .page-header,
.governance-page[dir="rtl"] th,
.governance-page[dir="rtl"] td {
  text-align: right;
}

.governance-page[dir="ltr"] .page-header,
.governance-page[dir="ltr"] th,
.governance-page[dir="ltr"] td {
  text-align: left;
}

.governance-page[dir="rtl"] .axis-indicator-value em {
  margin-right: auto;

  margin-left: 0;
}

.governance-page[dir="ltr"] .axis-indicator-value em {
  margin-left: auto;

  margin-right: 0;
}

.governance-page[dir="rtl"] .axis-indicator-card.flag::after {
  left: 14px;

  right: auto;
}

.governance-page[dir="ltr"] .axis-indicator-card.flag::after {
  right: 14px;

  left: auto;
}

.mobile-governance-cards {
  display: none;
}

@media (max-width: 768px) {
  .governance-page {
    padding: 12px 8px 28px;

    overflow-x: hidden;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .page-header h1 {
    font-size: 20px;

    line-height: 1.5;
  }

  .page-header p {
    font-size: 12px;

    line-height: 1.8;
  }

  .axis-summary {
    padding: 13px;

    border-radius: 15px;
  }

  .axis-card-header {
    flex-direction: column;

    align-items: stretch;

    gap: 12px;
  }

  .axis-score {
    width: 100%;

    min-width: 0;

    padding: 13px;
  }

  .axis-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    gap: 7px;
  }

  .axis-stats div {
    padding: 10px 5px;
  }

  .axis-stats b {
    font-size: 21px;
  }

  .axis-stats span {
    font-size: 10px;

    line-height: 1.5;
  }

  .axis-indicator-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .axis-indicator-card {
    padding: 13px;
  }

  .axis-indicator-card h3 {
    min-height: 0;

    font-size: 14px;
  }

  .axis-indicator-value {
    flex-wrap: wrap;
  }

  .axis-indicator-value em {
    width: 100%;

    margin: 0 !important;

    text-align: center;
  }

  .axis-mini-chart {
    height: auto;

    min-height: 280px;

    padding: 8px;

    overflow-x: auto;
  }

  .axis-card {
    padding: 12px 8px;
  }

  .charts-grid,
  .kpi-grid,
  .measured-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .governance-table {
    display: none;
  }

  .table-wrap {
    overflow: visible;

    border: 0;

    background: transparent;
  }

  .table-title {
    padding: 12px 13px;

    border-radius: 12px;

    font-size: 14px;
  }

  .mobile-governance-cards {
    display: grid;

    gap: 12px;

    margin-top: 12px;
  }

  .mobile-governance-card {
    padding: 14px;

    background: #fff;

    border: 1px solid #dbe3ee;

    border-inline-start: 5px solid #be185d;

    border-radius: 15px;

    box-shadow: 0 6px 18px rgba(15, 35, 64, 0.06);
  }

  .mobile-card-head {
    padding-bottom: 12px;

    border-bottom: 1px solid #e2e8f0;
  }

  .mobile-card-head h3 {
    margin: 0;

    color: #be185d;

    font-size: 15px;

    font-weight: 900;

    line-height: 1.7;

    overflow-wrap: break-word;
  }

  .mobile-card-body {
    display: grid;
  }

  .mobile-data-row {
    display: flex;

    align-items: center;

    justify-content: space-between;

    gap: 14px;

    padding: 10px 0;

    border-bottom: 1px dashed #e2e8f0;
  }

  .mobile-data-row:last-child {
    border-bottom: 0;
  }

  .mobile-data-row > span:first-child {
    color: #64748b;

    font-size: 12px;

    font-weight: 800;
  }

  .mobile-data-row\.stacked {
    align-items: stretch;

    flex-direction: column;

    gap: 6px;
  }

  .mobile-data-row strong {
    color: #071126;

    font-size: 13px;

    line-height: 1.8;

    overflow-wrap: anywhere;
  }

  .mobile-empty {
    padding: 24px;

    text-align: center;

    color: #64748b;

    background: #fff;

    border: 1px dashed #cbd5e1;

    border-radius: 14px;

    font-weight: 800;
  }

  .loader-card {
    width: calc(100% - 28px);

    padding: 28px 18px;
  }
}

@media (max-width: 380px) {
  .axis-stats {
    grid-template-columns: 1fr;
  }
}
</style>
